"use client"

import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/20/solid"
import { foundingTraderCampaignCopy } from "@src/config/foundingTraderCampaign"
import { useConnectWallet } from "@src/hooks/useConnectWallet"
import { useVipSupportEligibility } from "@src/hooks/useVipSupportEligibility"
import { useVipSupportStore } from "@src/stores/useVipSupportStore"
import { logger } from "@src/utils/logger"
import { useCallback, useState } from "react"
import Button from "./Button"

type VipSupportCardProps = {
  dismissible?: boolean
}

export function VipSupportCard({ dismissible = true }: VipSupportCardProps) {
  const { state } = useConnectWallet()
  const { isDismissed, dismiss } = useVipSupportStore()
  const shouldHideDismissed = dismissible && isDismissed
  const { isEligible, isLoading } = useVipSupportEligibility(
    state.isAuthorized && !shouldHideDismissed ? state.address : undefined
  )
  const [isRequesting, setIsRequesting] = useState(false)

  const handleRequestSupport = useCallback(async () => {
    const popup = window.open("about:blank", "_blank")
    setIsRequesting(true)
    try {
      const res = await fetch("/api/vip-support/link", { method: "POST" })
      if (!res.ok) {
        logger.error("Failed to get VIP support link", {
          status: res.status,
        })
        popup?.close()
        return
      }
      const data = await res.json()
      if (data.url && popup) {
        popup.opener = null
        popup.location.href = data.url
      } else {
        popup?.close()
      }
    } catch (error) {
      logger.error("VIP support request failed", { error })
      popup?.close()
    } finally {
      setIsRequesting(false)
    }
  }, [])

  if (!state.isAuthorized || isLoading || !isEligible || shouldHideDismissed) {
    return null
  }

  return (
    <section className="-outline-offset-1 relative rounded-2xl bg-gray-800 p-6 outline outline-transparent sm:p-9 dark:bg-gray-900 dark:outline-white/5">
      <div className="mx-auto max-w-lg space-y-3">
        <h3 className="font-semibold text-white text-xl/7 tracking-tight">
          {foundingTraderCampaignCopy.heading}
        </h3>

        <div className="space-y-4 text-pretty font-medium text-base/6 text-gray-300 dark:text-gray-400">
          <p>
            Given your activity on near.com, you've been selected as one of ten
            accounts invited to become a{" "}
            <span className="font-semibold text-white">Founding Trader</span>,
            receiving the following and more:
          </p>

          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <CheckCircleIcon className="mt-0.5 size-5 shrink-0 text-green-500" />
              <p>
                <span className="font-semibold text-white">
                  A private Telegram channel
                </span>{" "}
                with our leadership—for support, urgent announcements and
                feedback to help shape the future of near.com.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircleIcon className="mt-0.5 size-5 shrink-0 text-green-500" />
              <p>
                <span className="font-semibold text-white">
                  In-person time with NEAR leadership
                </span>{" "}
                at curated moments through the year.
              </p>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircleIcon className="mt-0.5 size-5 shrink-0 text-green-500" />
              <p>
                <span className="font-semibold text-white">
                  Access to early release features
                </span>{" "}
                before publicly announced.
              </p>
            </li>
          </ul>
          <p>
            The programme is anonymous, with no obligations. Begin the
            onboarding process below.
          </p>

          <Button
            onClick={handleRequestSupport}
            disabled={isRequesting}
            loading={isRequesting}
            className="dark"
            size="lg"
          >
            {foundingTraderCampaignCopy.primaryCta}
          </Button>
        </div>
      </div>

      {dismissible && (
        <button
          type="button"
          className="absolute top-4 right-4 z-1 flex size-9 items-center justify-center rounded-lg text-white hover:bg-white/10"
          onClick={dismiss}
        >
          <span className="sr-only">Dismiss</span>
          <XMarkIcon className="size-5" />
        </button>
      )}
    </section>
  )
}
